import { Injectable } from '@angular/core';

export interface FailedMeterExportRow {
  meterNumber?: string;
  meterId?: string;
  error?: string;
  totalImages?: number;
  status?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MeterWorkflowService {
  getParsedStorageItem<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch (error) {
      console.error(`Failed to parse ${key} from localStorage`, error);
      return fallback;
    }
  }

  getStoredMeter<T extends { readingPeriod?: string | null }>(key = 'currentMeter'): T | null {
    const meter = this.getParsedStorageItem<T | null>(key, null);
    if (!meter) return null;

    return {
      ...meter,
      readingPeriod: this.normalizeReadingPeriod(meter.readingPeriod),
    };
  }

  getCurrentUser<T = any>(key = 'currentUser'): T | null {
    return this.getParsedStorageItem<T | null>(key, null);
  }

  getCurrentPropertyId(fallback = ''): string {
    return localStorage.getItem('currentPropertyId') || fallback;
  }

  isValidReadingPeriod(period: string): boolean {
    if (!/^\d{6}$/.test(period)) return false;

    const year = Number(period.slice(0, 4));
    const month = Number(period.slice(4, 6));

    return year >= 2000 && year <= 2100 && month >= 1 && month <= 12;
  }

  normalizeReadingPeriod(period: string | null | undefined): string {
    const value = String(period || '').trim();

    if (this.isValidReadingPeriod(value)) {
      return value;
    }

    if (/^\d{6}$/.test(value)) {
      const yy = value.slice(0, 2);
      const mm = value.slice(2, 4);

      if (Number(mm) >= 1 && Number(mm) <= 12) {
        return `20${yy}${mm}`;
      }
    }

    return '';
  }

  loadMeterStatusListFromStorage<T = any>(key = 'meters'): T[] {
    const parsed: any = this.getParsedStorageItem<any>(key, null);
    const list =
      parsed?.getPropertyMeterPeriodStatusListV2?.meterPeriodStatusList ??
      parsed?.meterPeriodStatusList ??
      [];

    return Array.isArray(list) ? (list as T[]) : [];
  }

  getMeterDisplayName(meter: any): string {
    return (
      meter?.meterNumber ||
      meter?.meterNo ||
      meter?.meter_number ||
      meter?.meter?.meterNumber ||
      meter?.meter?.meterNo ||
      `Meter ${meter?.meterId?.slice?.(0, 8) || ''}`
    );
  }

  getFriendlySyncError(error: any): string {
    const raw =
      typeof error === 'string'
        ? error
        : error?.message || error?.error || error?.statusText || '';

    const msg = (raw || '').toString().toLowerCase();

    if (!raw) {
      return 'Something went wrong while syncing this meter.';
    }

    if (
      msg.includes('network') ||
      msg.includes('failed to fetch') ||
      msg.includes('offline') ||
      msg.includes('internet')
    ) {
      return 'No internet connection. The reading may be saved locally. Please reconnect and sync again.';
    }

    if (msg.includes('timeout') || msg.includes('timed out')) {
      return 'The upload took too long. Please try again.';
    }

    if (
      msg.includes('403') ||
      msg.includes('forbidden') ||
      msg.includes('unauthorized') ||
      msg.includes('401')
    ) {
      return 'You do not have permission to upload this meter right now.';
    }

    if (
      msg.includes('500') ||
      msg.includes('502') ||
      msg.includes('503') ||
      msg.includes('504') ||
      msg.includes('server')
    ) {
      return 'The server is currently unavailable. Please try again shortly.';
    }

    if (
      msg.includes('image') ||
      msg.includes('upload') ||
      msg.includes('file') ||
      msg.includes('s3') ||
      msg.includes('presigned') ||
      msg.includes('multipart')
    ) {
      return 'The meter reading was captured, but one or more images failed to upload. You can retry syncing the images.';
    }

    if (msg.includes('validation') || msg.includes('invalid')) {
      return 'Some meter data is invalid. Please review the reading and try again.';
    }

    if (msg.includes('reading not within target date')) {
      return 'Reading date is outside the allowed period.';
    }

    return `${error}`;
  }

  saveLastSync(key = 'lastSync'): string {
    const formatted = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    localStorage.setItem(key, formatted);
    return formatted;
  }

  buildFailedMetersCsv(rows: FailedMeterExportRow[]): string {
    const escapeCsv = (value: unknown): string => {
      const stringValue = String(value ?? '');
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = ['Meter No', 'ID', 'Error', 'Total Images', 'Status'];
    const dataRows = rows.map((row) => [
      row.meterNumber || '',
      row.meterId || '',
      row.error || '',
      row.totalImages || '',
      row.status || 'FAILED',
    ]);

    return [headers, ...dataRows]
      .map((line) => line.map(escapeCsv).join(','))
      .join('\n');
  }
}
